/**
 * Properties of an Organization (Betrieb / Hof / ...).
 *
 * @note The interfaces in this file are more or less copied from the supabase generated types in the users-svc.
 */

import { array, object, string, type InferType } from "yup"
import { MachineSupaSchema } from "../machines/Machine"
import { AnimalGroupSchema } from "../recipes/AnimalGroup"
import { FeedComponentSchema } from "../recipes/FeedComponent"
import { RecipeSchema } from "../recipes/Recipe"
import { UserOrgsRolesSchema } from "../users/UserOrgs"
import { AddressSchema } from "./Address"

export const OrganizationSchemaNameMaxLength = 30
export const OrganizationSchema = object({
  address: AddressSchema,
  created_at: string().required(),
  created_by: string().required(),
  ext_id: string().nullable(),
  id: string().required(),
  name: string().required().max(OrganizationSchemaNameMaxLength),
  updated_at: string().defined(),
  updated_by: string().defined(),

  user_role: UserOrgsRolesSchema.optional(),

  FeedComponents: array(FeedComponentSchema).required(),
  Ausladegruppen: array(AnimalGroupSchema).required(),
  Recipes: array(RecipeSchema).required(),
  Machines: array(MachineSupaSchema).required(),
  Users: array().optional(), // not typed, to avoid circular reference to GroupDataSchema

  // [moarProps: string]: any
})

/**
 * Update-DTO: All data that's transferred from FE to BE for a update request.
 *
 * @note Of course, `id` is required for an update. It is taken from the path.
 */
export const OrgSettingsUpdateSchema = OrganizationSchema.omit([
  "created_at", // cannot be updated
  "created_by", // cannot be updated
  "updated_at", // filled by backend
  "updated_by", // filled by backend
  "id", // cannot be updated
  "Ausladegruppen", // not part of org settings
  "FeedComponents", // not part of org settings
  "Recipes", // not part of org settings
  "Machines", // not part of org settings
  "Users", // not part of org settings
  "user_role", // not part of org settings
])

/**
 * Create-DTO for self-service: a user without an org creates its own.
 * Only name and address; e.g. `ext_id` is reserved for admins.
 */
export const OwnOrgCreateSchema = OrgSettingsUpdateSchema.pick([
  "name",
  "address",
])

export type OrganizationDto = InferType<typeof OrganizationSchema>
export type OrgSettingsUpdateDto = InferType<typeof OrgSettingsUpdateSchema>
export type OwnOrgCreateDto = InferType<typeof OwnOrgCreateSchema>
